import React from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { StepProps, DamagedPart, DAMAGED_PART_LABELS } from '../../types';

const Container = styled.div`
  ${tw`flex flex-col h-full`}
`;

const SectionLabel = styled.div`
  ${tw`text-xs font-semibold text-text-secondary uppercase tracking-wider mb-3`}
`;

const Title = styled.h2`
  ${tw`text-xl font-semibold text-text-primary mb-2`}
`;

const Subtitle = styled.p`
  ${tw`text-sm text-text-secondary mb-6`}
`;

const CategorySection = styled.div`
  ${tw`mb-6`}
`;

const CategoryTitle = styled.h3`
  ${tw`text-sm font-semibold text-text-secondary mb-3`}
`;

const CheckboxGrid = styled.div`
  ${tw`grid grid-cols-1 gap-2`}
`;

const CheckboxItem = styled.button<{ isSelected: boolean }>`
  ${tw`w-full p-3 text-left border rounded-lg transition-all flex items-center gap-3`}
  ${props =>
    props.isSelected
      ? tw`border-black bg-pink-50`
      : tw`border-border-default hover:border-border-default`}
`;

const Checkbox = styled.div<{ isSelected: boolean }>`
  ${tw`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0`}
  ${props => (props.isSelected ? tw`border-black bg-black` : tw`border-border-default`)}
`;

const CheckboxLabel = styled.span`
  ${tw`text-sm text-text-primary`}
`;

const OtherInput = styled.input`
  ${tw`w-full mt-2 p-2 border border-border-default rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black`}
`;

const ContinueButton = styled.button<{ disabled: boolean }>`
  ${tw`w-full py-4 font-semibold rounded-card mt-auto`}
  ${props =>
    props.disabled
      ? tw`bg-bg-secondary text-text-tertiary cursor-not-allowed`
      : tw`bg-black text-white hover:bg-black-hover`}
`;

const partsCategories = [
  {
    title: 'Body Damage',
    parts: [
      DamagedPart.DRIVER_SIDE_BODY,
      DamagedPart.FRONT_BUMPER_OR_GRILLE,
      DamagedPart.FRONT_HOOD,
      DamagedPart.PASSENGER_SIDE_BODY,
      DamagedPart.REAR_BUMPER,
      DamagedPart.ROOF,
      DamagedPart.TRUNK,
    ],
  },
  {
    title: 'Tire & Rim',
    parts: [
      DamagedPart.DRIVER_SIDE_FRONT_TIRE_OR_RIM,
      DamagedPart.DRIVER_SIDE_REAR_TIRE_OR_RIM,
      DamagedPart.PASSENGER_SIDE_FRONT_TIRE_OR_RIM,
      DamagedPart.PASSENGER_SIDE_REAR_TIRE_OR_RIM,
    ],
  },
  {
    title: 'Windows & Mirrors',
    parts: [
      DamagedPart.DRIVER_SIDE_WINDOWS_OR_MIRRORS,
      DamagedPart.PASSENGER_SIDE_WINDOWS_OR_MIRRORS,
      DamagedPart.FRONT_WINDSHIELD,
      DamagedPart.REAR_WINDSHIELD,
    ],
  },
  {
    title: 'Other',
    parts: [
      DamagedPart.INTERIOR_DAMAGE,
      DamagedPart.MECHANICAL_DAMAGE,
      DamagedPart.HEADLIGHTS_OR_TAIL_LIGHTS,
      DamagedPart.OTHER,
    ],
  },
];

const DamagedPartsStep: React.FC<StepProps> = ({ formData, updateFormData, onNext }) => {
  const handleTogglePart = (part: DamagedPart) => {
    const currentParts = formData.damagedParts;
    const isSelected = currentParts.includes(part);

    if (isSelected) {
      updateFormData({
        damagedParts: currentParts.filter(p => p !== part),
      });
    } else {
      updateFormData({
        damagedParts: [...currentParts, part],
      });
    }
  };

  const handleOtherDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateFormData({ damagedPartsOtherDescription: e.target.value });
  };

  const canContinue = formData.damagedParts.length > 0;

  const showOtherInput = formData.damagedParts.includes(DamagedPart.OTHER);

  return (
    <Container>
      <SectionLabel>DAMAGE DETAILS</SectionLabel>
      <Title>What parts of the vehicle were damaged?</Title>
      <Subtitle>Please select at least one, and check all of the options that apply.</Subtitle>

      {partsCategories.map(category => (
        <CategorySection key={category.title}>
          <CategoryTitle>{category.title}</CategoryTitle>
          <CheckboxGrid>
            {category.parts.map(part => (
              <React.Fragment key={part}>
                <CheckboxItem
                  isSelected={formData.damagedParts.includes(part)}
                  onClick={() => handleTogglePart(part)}
                >
                  <Checkbox isSelected={formData.damagedParts.includes(part)}>
                    {formData.damagedParts.includes(part) && (
                      <FontAwesomeIcon icon={faCheck} size="xs" color="white" />
                    )}
                  </Checkbox>
                  <CheckboxLabel>{DAMAGED_PART_LABELS[part]}</CheckboxLabel>
                </CheckboxItem>
                {part === DamagedPart.OTHER && showOtherInput && (
                  <OtherInput
                    placeholder="Please describe the other damage"
                    value={formData.damagedPartsOtherDescription || ''}
                    onChange={handleOtherDescriptionChange}
                  />
                )}
              </React.Fragment>
            ))}
          </CheckboxGrid>
        </CategorySection>
      ))}

      <ContinueButton disabled={!canContinue} onClick={onNext}>
        Continue
      </ContinueButton>
    </Container>
  );
};

export default DamagedPartsStep;
